import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Tabs, Tab, Button, IconButton, Switch, Paper, Stack, Chip, Avatar,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  CircularProgress, InputAdornment, Divider,Grid,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

const API = process.env.REACT_APP_API_LAUNDRY_URL;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } });
const TEAL = "#006D77";
const DRAWER_WIDTH = 280;
const TABS = ["Services", "Items", "Pricing"];
const CATEGORIES = ["Men", "Women", "Kids", "Home"];
const UNITS = ["Per Piece", "Per Set"];
const STATUSES = [
  "pending", "confirmed", "pickup_assigned", "pickup_completed",
  "received_at_laundry", "washing", "drying", "ironing",
  "quality_check", "packed", "out_for_delivery", "delivered", "cancelled",
];
const statusLabel = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const LaundryAdmin = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const community = sessionStorage.getItem("community") || "";

  return (
    <Box sx={{
      minHeight: "100vh", bgcolor: "#EEF4F5",
      ml: { xs: 0, md: `${DRAWER_WIDTH}px` }, mt: { xs: "56px", md: 0 },
      px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 },
    }}>
        
      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
          sx={{ mb: 2, textTransform: "none", color: TEAL, fontWeight: 600 }}>Back</Button>
        <Typography variant="h4" fontWeight={800} color={TEAL} gutterBottom
          sx={{ fontSize: { xs: "1.6rem", sm: "2.125rem" } }}>Laundry Service — Admin</Typography>
        <Typography color="text.secondary" mb={3}>
          Manage services, items, pricing, and track orders{community ? ` for ${community}` : ""}.
        </Typography>

        <Paper variant="outlined" sx={{ borderRadius: 3 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: "divider", "& .Mui-selected": { color: TEAL } }}
            TabIndicatorProps={{ style: { backgroundColor: TEAL } }}>
            {TABS.map((l) => <Tab key={l} label={l} sx={{ textTransform: "none", fontWeight: 600 }} />)}
          </Tabs>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {tab === 0 && <ServiceManager community={community} />}
            {tab === 1 && <ItemManager community={community} />}
            {tab === 2 && <PriceManager community={community} />}
            {/* {tab === 3 && <OrderManager community={community} />} */}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

// ============================================================ Services (image upload)
const emptyService = { name: "", description: "", image_url: "", community: "" };
const ServiceManager = ({ community }) => {
  const [services, setServices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyService);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/dashboard/services`, { ...authHeaders(), params: { community } })
    .then((r) => setServices(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openAdd = () => {
    setEditingId(null); setForm({ ...emptyService, community });
    setImageFile(null); setImagePreview(""); setShowForm(true);
  };
  const openEdit = (s) => {
    setEditingId(s.id); setForm({ ...s });
    setImageFile(null); setImagePreview(s.image_url || ""); setShowForm(true);
  };
  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file); setImagePreview(URL.createObjectURL(file));
  };

  const save = async () => {
    setSaving(true);
    try {
      let id = editingId;
      if (editingId) await axios.put(`${API}/dashboard/services/${editingId}`, form, authHeaders());
      else { const res = await axios.post(`${API}/dashboard/services`, { ...form, community }, authHeaders()); id = res.data.id; }
      if (imageFile && id) {
        const fd = new FormData(); fd.append("image", imageFile);
        await axios.post(`${API}/dashboard/services/${id}/image`, fd, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}`, "Content-Type": "multipart/form-data" },
        });
      }
      setShowForm(false); load();
    } finally { setSaving(false); }
  };
  const toggle = async (s) => { await axios.put(`${API}/dashboard/services/${s.id}`, { is_active: !s.is_active }, authHeaders()); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this service?")) return; await axios.delete(`${API}/dashboard/services/${id}`, authHeaders()); load(); };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={700}>Services</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
          sx={{ bgcolor: TEAL, textTransform: "none" }}>Add Service</Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: "#F8FAFC" }}>
            <TableRow><TableCell>Service</TableCell><TableCell>Description</TableCell>
              <TableCell>Active</TableCell><TableCell align="right">Actions</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {services.map((s) => (
              <TableRow key={s.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar variant="rounded" src={s.image_url} sx={{ bgcolor: "#E0F2F1", width: 44, height: 44 }}>🧺</Avatar>
                    <Typography fontWeight={700}>{s.name}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>{s.description}</TableCell>
                <TableCell><Switch checked={s.is_active} onChange={() => toggle(s)} /></TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openEdit(s)} sx={{ color: TEAL }}><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => remove(s.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {services.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ color: "text.secondary" }}>No services yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={showForm} onClose={() => setShowForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>{editingId ? "Edit Service" : "Add Service"}</DialogTitle>
        <DialogContent>
          <Stack alignItems="center" spacing={1.5} sx={{ mb: 2, mt: 1 }}>
            <Avatar variant="rounded" src={imagePreview} sx={{ width: 120, height: 90, bgcolor: "#E0F2F1", fontSize: 34 }}>🧺</Avatar>
            <Button component="label" startIcon={<PhotoCameraIcon />} size="small" sx={{ textTransform: "none", color: TEAL }}>
              Upload Image
              <input hidden accept="image/*" type="file" onChange={onPickImage} />
            </Button>
          </Stack>
          <TextField fullWidth margin="dense" label="Name (e.g. Wash & Fold)" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField fullWidth margin="dense" label="Description" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} multiline rows={2} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowForm(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" disabled={!form.name || saving} onClick={save}
            sx={{ bgcolor: TEAL, textTransform: "none" }}>{saving ? "Saving..." : "Save"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ============================================================ Items
const emptyItem = { category: "Men", name: "", unit: "Per Piece", image_url: "", community: "" };
const ItemManager = ({ community }) => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyItem);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/dashboard/items`, { ...authHeaders(), params: { community } })
    .then((r) => setItems(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openAdd = () => {
    setEditingId(null); setForm({ ...emptyItem, community });
    setImageFile(null); setImagePreview(""); setShowForm(true);
  };
  const openEdit = (i) => {
    setEditingId(i.id); setForm({ ...i });
    setImageFile(null); setImagePreview(i.image_url || ""); setShowForm(true);
  };
  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file); setImagePreview(URL.createObjectURL(file));
  };

  const save = async () => {
    setSaving(true);
    try {
      let id = editingId;
      if (editingId) await axios.put(`${API}/dashboard/items/${editingId}`, form, authHeaders());
      else { const res = await axios.post(`${API}/dashboard/items`, { ...form, community }, authHeaders()); id = res.data.id; }
      if (imageFile && id) {
        const fd = new FormData(); fd.append("image", imageFile);
        await axios.post(`${API}/dashboard/items/${id}/image`, fd, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}`, "Content-Type": "multipart/form-data" },
        });
      }
      setShowForm(false); load();
    } finally { setSaving(false); }
  };
  const toggle = async (i) => { await axios.put(`${API}/dashboard/items/${i.id}`, { is_active: !i.is_active }, authHeaders()); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this item?")) return; await axios.delete(`${API}/dashboard/items/${id}`, authHeaders()); load(); };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={700}>Clothing Items</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
          sx={{ bgcolor: TEAL, textTransform: "none" }}>Add Item</Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: "#F8FAFC" }}>
            <TableRow><TableCell>Category</TableCell><TableCell>Item</TableCell>
              <TableCell>Unit</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} hover>
                <TableCell><Chip label={i.category} size="small" sx={{ bgcolor: "#E0F7F8", color: TEAL, fontWeight: 700 }} /></TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar variant="rounded" src={i.image_url} sx={{ bgcolor: "#E0F2F1", width: 36, height: 36 }}>👕</Avatar>
                    <Typography fontWeight={600}>{i.name}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>{i.unit}</TableCell>
                <TableCell><Switch checked={i.is_active} onChange={() => toggle(i)} /></TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openEdit(i)} sx={{ color: TEAL }}><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => remove(i.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>No items yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={showForm} onClose={() => setShowForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>{editingId ? "Edit Item" : "Add Item"}</DialogTitle>
        <DialogContent>
          <Stack alignItems="center" spacing={1.5} sx={{ mb: 2, mt: 1 }}>
            <Avatar variant="rounded" src={imagePreview} sx={{ width: 100, height: 80, bgcolor: "#E0F2F1", fontSize: 28 }}>👕</Avatar>
            <Button component="label" startIcon={<PhotoCameraIcon />} size="small" sx={{ textTransform: "none", color: TEAL }}>
              Upload Image
              <input hidden accept="image/*" type="file" onChange={onPickImage} />
            </Button>
          </Stack>
          <TextField select fullWidth margin="dense" label="Category" value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField fullWidth margin="dense" label="Item name (e.g. Shirt)" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField select fullWidth margin="dense" label="Unit" value={form.unit}
            onChange={(e) => setForm({ ...form, unit: e.target.value })}>
            {UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowForm(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" disabled={!form.name || saving} onClick={save}
            sx={{ bgcolor: TEAL, textTransform: "none" }}>{saving ? "Saving..." : "Save"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ============================================================ Pricing
const PriceManager = ({ community }) => {
  const [services, setServices] = useState([]);
  const [items, setItems] = useState([]);
  const [prices, setPrices] = useState([]);
  const [service, setService] = useState("");
  const [priceMap, setPriceMap] = useState({});
  const [saving, setSaving] = useState(false);

  const loadPrices = () => axios.get(`${API}/dashboard/prices`, { ...authHeaders(), params: { community } })
    .then((r) => setPrices(r.data || []));

  useEffect(() => {
    axios.get(`${API}/dashboard/services`, { ...authHeaders(), params: { community } })
      .then((r) => { setServices(r.data || []); if (r.data.length) setService(r.data[0].name); });
    axios.get(`${API}/dashboard/items`, { ...authHeaders(), params: { community } })
      .then((r) => setItems(r.data || []));
    loadPrices();
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    const map = {};
    items.forEach((it) => {
      const existing = prices.find((p) => p.service === service && p.item === it.name);
      map[it.name] = existing ? String(existing.price) : "";
    });
    setPriceMap(map);
    /* eslint-disable-next-line */
  }, [service, prices, items]);

  const savePrice = async (itemName) => {
    setSaving(true);
    try {
      await axios.post(`${API}/dashboard/prices`, {
        service, item: itemName, price: parseFloat(priceMap[itemName]) || 0, community,
      }, authHeaders());
      loadPrices();
    } finally { setSaving(false); }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={2}>Pricing</Typography>
      <TextField select label="Service" value={service} onChange={(e) => setService(e.target.value)}
        sx={{ minWidth: { xs: "100%", sm: 260 }, mb: 3 }} size="small">
        {services.map((s) => <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>)}
      </TextField>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: "#F8FAFC" }}>
            <TableRow><TableCell>Category</TableCell><TableCell>Item</TableCell>
              <TableCell>Unit</TableCell><TableCell>Price (₹)</TableCell><TableCell align="right">Save</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} hover>
                <TableCell>{it.category}</TableCell>
                <TableCell><Typography fontWeight={600}>{it.name}</Typography></TableCell>
                <TableCell>{it.unit}</TableCell>
                <TableCell>
                  <TextField type="number" size="small" value={priceMap[it.name] ?? ""}
                    onChange={(e) => setPriceMap({ ...priceMap, [it.name]: e.target.value })}
                    sx={{ width: 120 }} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" variant="contained" disabled={saving} onClick={() => savePrice(it.name)}
                    sx={{ bgcolor: TEAL, textTransform: "none" }}>Save</Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>Add items first.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};



export default LaundryAdmin;